import clsx from 'clsx';
import React from 'react';
import { IconButton } from './Button';
import { IconMenu } from './icons';

/**
 * Lets a frame with a collapsible sidebar expose its "open navigation"
 * action. Without a provider, PageHeader renders no menu button.
 * `sidebarHidden` keeps the button on desktop while the sidebar is hidden.
 * `frameProvidesNav` means the frame already has a menu button (the store
 * bar on pages that have not filled the frame), so the title does not add one.
 */
export const PageHeaderNavContext = React.createContext<{
  open: () => void;
  sidebarHidden?: boolean;
  frameProvidesNav?: boolean;
} | null>(null);

const MobileNavButton: React.FC = () => {
  const nav = React.useContext(PageHeaderNavContext);
  if (!nav || nav.frameProvidesNav) return null;
  return (
    <IconButton
      label="Open navigation"
      onClick={nav.open}
      className={clsx(!nav.sidebarHidden && 'lg:hidden')}
    >
      <IconMenu />
    </IconButton>
  );
};

/**
 * Page title on the canvas: bold title (18px on phones, 22px from `sm`),
 * an optional one-line description, short meta, and actions. It is not a
 * card. Boxed content — a list, a workspace — sits below it in a `Surface`.
 * On phones the actions wrap under the title rather than squeezing it.
 *
 * `compact` is accepted so existing call sites keep compiling; the title is
 * no longer a sized bar.
 */
export const PageHeader: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  compact?: boolean;
}> = ({ title, description, meta, actions, children }) => (
  <header>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <MobileNavButton />
      <div className="min-w-[min(100%,10rem)] flex-1">
        <div className="flex min-w-0 items-baseline gap-3">
          <h1 className="truncate text-v2-18 font-bold tracking-[-0.015em] text-v2-fg sm:text-v2-22 sm:tracking-[-0.02em]">
            {title}
          </h1>
          {meta && (
            <span className="v2-num truncate text-v2-12 text-v2-fg-3 sm:text-v2-13">
              {meta}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 max-w-3xl text-v2-13 text-v2-fg-3">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="ml-auto flex max-w-full items-center gap-2">
          {actions}
        </div>
      )}
    </div>
    {children}
  </header>
);

/**
 * Toolbar band at the top of a list card: the search leads on the left,
 * then filters. Headline figures don't go here; they stand above the card
 * (`ListPage`'s `summary`).
 * One padding rhythm for every list so the toolbar sits with the same air
 * above the table on Reservations, Orders, Calls and Customers alike.
 */
export const PageHeaderBand: React.FC<{
  children: React.ReactNode;
  className?: string;
  /** Less air between the toolbar and the table, for a short metric row. */
  compact?: boolean;
}> = ({ children, className, compact }) => (
  <div
    className={clsx(
      'flex flex-wrap items-center px-4 lg:px-6',
      compact
        ? 'gap-x-4 gap-y-2.5 py-2.5 lg:py-3'
        : 'gap-x-8 gap-y-4 py-4 lg:py-5',
      className,
    )}
  >
    {children}
  </div>
);
